import './util/handleError';
import './global/init';
import './global/actions/initial';

import React from './lib/teact/teact';
import TeactDOM from './lib/teact/teact-dom';
import {
  getActions, getGlobal,
} from './global';

import {
  DEBUG, STRICTERDOM_ENABLED,
} from './config';
import { enableStrict, requestMutation } from './lib/fasterdom/fasterdom';
import { selectTabState } from './global/selectors/tabs';
import { selectSharedSettings } from './global/selectors/sharedState';
import { requestGlobal, subscribeToMultitabBroadcastChannel } from './util/browser/multitab';
import { establishMultitabRole, subscribeToMasterChange } from './util/establishMultitabRole';
import { initGlobal } from './util/init';
import { initLocalization } from './util/localization';
import { MULTITAB_STORAGE_KEY } from './util/multiaccount';
import { initChannelCode } from './util/nativeBridge';
import { checkAndAssignPermanentWebVersion } from './util/permanentWebVersion';
import { initSafeArea } from './util/safeArea';
import { Bundles, loadBundle } from './util/moduleLoader';
import { onBeforeUnload } from './util/schedulers';
import { markStartupPerf, printStartupPerfSummary } from './util/startupPerf';

import App from './components/App';

import './assets/fonts/roboto.css';
import './styles/index.scss';

if (STRICTERDOM_ENABLED) {
  enableStrict();
}

window.setTimeout(() => {
  markStartupPerf('init_scheduled');
  void init();
}, 0);

function removeSplashScreen() {
  window.requestAnimationFrame(() => {
    markStartupPerf('splash_remove_start');
    document.getElementById('splash')?.remove();
    markStartupPerf('splash_removed');
    printStartupPerfSummary();
  });
}

function runPostMountTasks() {
  window.requestAnimationFrame(() => {
    window.setTimeout(() => {
      markStartupPerf('post_mount_tasks_start');
      if (getGlobal().authState !== 'authorizationStateReady') {
        markStartupPerf('auth_bundle_prefetch_scheduled');
        void loadBundle(Bundles.Auth);
      }

      void import('./util/updateWebmanifest').then(({ default: updateWebmanifest }) => {
        markStartupPerf('update_webmanifest_loaded');
        updateWebmanifest();
      });
      void import('./util/betterView').then(({ betterView }) => {
        markStartupPerf('better_view_loaded');
        betterView();
      });
      void import('./util/setupServiceWorker').then(({ default: setupServiceWorker }) => {
        markStartupPerf('service_worker_setup_loaded');
        setupServiceWorker();
      });
    }, 0);
  });
}

async function init() {
  markStartupPerf('init_start');

  if (DEBUG) {
    // eslint-disable-next-line no-console
    console.log('>>> INIT');
  }

  if (!(window as any).isCompatTestPassed) return;

  checkAndAssignPermanentWebVersion();

  initChannelCode();
  initSafeArea();

  await window.electron?.restoreLocalStorage();

  subscribeToMultitabBroadcastChannel();
  markStartupPerf('request_global_start');
  await requestGlobal(APP_VERSION);
  markStartupPerf('request_global_done');
  localStorage.setItem(MULTITAB_STORAGE_KEY, '1');
  onBeforeUnload(() => {
    const global = getGlobal();
    if (Object.keys(global.byTabId).length === 1) {
      localStorage.removeItem(MULTITAB_STORAGE_KEY);
    }
  });

  markStartupPerf('init_global_start');
  await initGlobal();
  markStartupPerf('init_global_done');
  getActions().init();
  markStartupPerf('actions_init_done');

  getActions().updateShouldEnableDebugLog();
  getActions().updateShouldDebugExportedSenders();

  const global = getGlobal();

  const sharedSettings = selectSharedSettings(global);
  const initialLanguage = sharedSettings.language || window.navigator.language || 'zh-CN';

  initLocalization(initialLanguage, true);

  subscribeToMasterChange((isMasterTab) => {
    getActions()
      .switchMultitabRole({ isMasterTab }, { forceSyncOnIOs: true });
  });
  const shouldReestablishMasterToSelf = getGlobal().authState !== 'authorizationStateReady';
  establishMultitabRole(shouldReestablishMasterToSelf);

  if (DEBUG) {
    // eslint-disable-next-line no-console
    console.log('>>> START INITIAL RENDER');
  }

  requestMutation(() => {
    markStartupPerf('react_render_start');
    TeactDOM.render(
      <App />,
      document.getElementById('root')!,
    );
    markStartupPerf('react_render_done');

    removeSplashScreen();
  });

  runPostMountTasks();
  markStartupPerf('post_mount_tasks_scheduled');

  if (DEBUG) {
    // eslint-disable-next-line no-console
    console.log('>>> FINISH INITIAL RENDER');
  }

  if (DEBUG) {
    document.addEventListener('dblclick', () => {
      // eslint-disable-next-line no-console
      console.warn('TAB STATE', selectTabState(getGlobal()));
      // eslint-disable-next-line no-console
      console.warn('GLOBAL STATE', getGlobal());
    });
  }
}

onBeforeUnload(() => {
  const actions = getActions();
  actions.leaveGroupCall?.({ isPageUnload: true });
  actions.hangUp?.({ isPageUnload: true });
});
